import { useCallback, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useAppDispatch } from "@/Redux/app/hooks";
import { delete_panel_notification, get_panel_notifications } from "@/Redux/Actions/Settings";
import { GetPanelNotificationsResponse, PanelInboxNotification, PanelNotificationEvent } from "@/types/panelNotifications";

const PAGE_LIMIT = 20;

const isSuccessPayload = (payload: unknown): payload is GetPanelNotificationsResponse =>
    typeof payload === "object" &&
    payload !== null &&
    (payload as GetPanelNotificationsResponse).code === 1;

const mergeNotificationPages = (
    previous: PanelInboxNotification[],
    nextPage: PanelInboxNotification[],
    reset: boolean
): PanelInboxNotification[] => {
    if (reset) return nextPage;
    const seen = new Set(previous.map((row) => row._id));
    const merged = [...previous];
    nextPage.forEach((row) => {
        if (!seen.has(row._id)) merged.push(row);
    });
    return merged;
};

export function usePanelNotificationInbox(userType?: string) {
    const dispatch = useAppDispatch();
    const router = useRouter();
    const [items, setItems] = useState<PanelInboxNotification[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const fetchInFlightRef = useRef(false);
    const nextPageRef = useRef(1);

    const hasMore = items.length < totalCount;

    const loadPage = useCallback(
        async (page: number, reset: boolean): Promise<boolean> => {
            if (fetchInFlightRef.current) return false;
            fetchInFlightRef.current = true;
            if (reset) setLoading(true);
            else setLoadingMore(true);

            try {
                const response = await dispatch(
                    get_panel_notifications({ page, limit: PAGE_LIMIT, search: "" })
                );
                const payload = response.payload as GetPanelNotificationsResponse | undefined;
                if (isSuccessPayload(payload)) {
                    const list = payload.data?.response ?? [];
                    const count = payload.data?.count ?? list.length;
                    setTotalCount(count);
                    setItems((prev) => mergeNotificationPages(prev, list, reset));
                    nextPageRef.current = page + 1;
                    return true;
                }
                return false;
            } finally {
                setLoading(false);
                setLoadingMore(false);
                fetchInFlightRef.current = false;
            }
        },
        [dispatch]
    );

    const fetchInbox = useCallback(async (): Promise<boolean> => {
        nextPageRef.current = 1;
        return loadPage(1, true);
    }, [loadPage]);

    const loadMoreInbox = useCallback(async () => {
        if (!hasMore || fetchInFlightRef.current || loading || loadingMore) return;
        await loadPage(nextPageRef.current, false);
    }, [hasMore, loadPage, loading, loadingMore]);

    const removeNotification = useCallback(
        async (id: string) => {
            const response = await dispatch(delete_panel_notification({ _id: id }));
            const payload = response.payload as GetPanelNotificationsResponse | undefined;
            if (isSuccessPayload(payload)) {
                setItems((prev) => prev.filter((row) => row._id !== id));
                setTotalCount((prev) => Math.max(0, prev - 1));
            }
        },
        [dispatch]
    );

    const navigateFromNotification = useCallback(
        (row: PanelInboxNotification) => {
            const meta = row.metadata ?? {};
            const event: PanelNotificationEvent | undefined = row.event ?? meta.event;

            if (event === "KITCHEN_ADMIN_ORDER_EXPIRING" && meta.orderMongoId) {
                router.push({
                    pathname: "/orders",
                    query: { tab: "orderIn", drawerOrderId: meta.orderMongoId },
                });
                return;
            }

            if (
                userType === "ADMIN" &&
                (event === "ADMIN_REPLACEMENT_CONFIRMED" || event === "ADMIN_REPLACEMENT_REFUND")
            ) {
                const search =
                    meta.childOrderId?.replace(/^#/, "") ||
                    meta.orderId?.replace(/^#/, "") ||
                    "";
                router.push({
                    pathname: "/orders",
                    query: {
                        tab: "replacementHistory",
                        notifySearch: search,
                        ...(meta.replacementRequestId
                            ? { openReplacementId: meta.replacementRequestId }
                            : {}),
                    },
                });
                return;
            }

            if (meta.childOrderId) {
                router.push({
                    pathname: "/orders",
                    query: { tab: "orderIn", drawerChildOrderId: meta.childOrderId },
                });
            }
        },
        [router, userType]
    );

    return {
        items,
        loading,
        loadingMore,
        hasMore,
        fetchInbox,
        loadMoreInbox,
        removeNotification,
        navigateFromNotification,
    };
}
